import { useLocalStorage } from "@mantine/hooks";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";

import { MASTER_INVALIDATION_KEY } from "@/api/queryClient";
import {
  apiConfigDeployments,
  createCustomerDeployment,
  createDeployment,
  deleteDeployment,
  updateDeployment,
} from "@/api/resources/deployments/api";
import { deploymentsQueries, deploymentsQueryKeys } from "@/api/resources/deployments/queries";
import { selectorCurrentDeployments, selectorFilteredDeployments } from "@/api/resources/deployments/selectors";
import { CACHED_DEPLOYMENT_KEY, getIdealDeployment } from "@/api/resources/deployments/utils";
import { useWorkspaceCustomers } from "@/api/resources/workspaces/hooks";
import { notify } from "@/hooks/useNotifications";
import i18n from "@/providers/i18n";
import { spawnCreateMutationHook, spawnDeleteMutationHook, spawnUpdateMutationHook } from "@/utils/api";
import { makeQueryFilterIn, type QueryFilter } from "@/utils/filter";

const entity = i18n.t("deployment");

export const useDeployments = () => {
  return useQuery(deploymentsQueries.listAll());
};

export const useDeploymentsWithRoles = () => {
  return useQuery(deploymentsQueries.listWithRoles());
};

export const useDeployment = (deployment_id?: string) => {
  return useQuery(deploymentsQueries.byId(deployment_id));
};

export const useCurrentDeployment = () => {
  const { deployment_id } = useParams({ strict: false });
  return useQuery(deploymentsQueries.byId(deployment_id));
};

export const useFilteredDeployments = (workspace_id?: string) => {
  const { workspace_id: _workspace_id } = useParams({ strict: false });
  const { data: workspaceCustomers } = useWorkspaceCustomers(workspace_id ?? _workspace_id);
  return useQuery({
    ...deploymentsQueries.listAll(),
    select: (deployments) =>
      selectorFilteredDeployments({
        currentWorkspaceId: workspace_id ?? _workspace_id,
        deployments,
        customerWorkspaces: workspaceCustomers ?? [],
      }),
  });
};

// Reactive cached-deployment source: writes here re-render every consumer in the same tab.
export const useCachedDeployment = () => {
  return useLocalStorage<string | null>({
    key: CACHED_DEPLOYMENT_KEY,
    defaultValue: null,
    getInitialValueInEffect: false,
  });
};

export const useIdealDeployment = (workspace_id?: string) => {
  const { data: deployments } = useFilteredDeployments(workspace_id);
  const [cachedDeploymentId] = useCachedDeployment();
  return getIdealDeployment(deployments ?? [], cachedDeploymentId);
};

export const useCurrentDeployments = () => {
  const { workspace_id } = useParams({ strict: false });
  return useQuery({
    ...deploymentsQueries.listAll(),
    select: (deployments) => selectorCurrentDeployments({ currentWorkspaceId: workspace_id, deployments }),
  });
};

export const useDeploymentsQueryFilters = () => {
  const { data: currentDeployment } = useCurrentDeployment();
  const { data: currentDeployments } = useCurrentDeployments();
  const { data: filteredDeployments } = useFilteredDeployments();

  return {
    currentDeployment: currentDeployment ? (["deployment_id", "eq", currentDeployment.id] as QueryFilter) : undefined,
    currentDeployments: makeQueryFilterIn({
      items: currentDeployments ?? [],
      filterKey: "deployment_id",
      byItemKey: "id",
      noShortCircuit: true, // used in Workspace Overview DataTables, otherwise it would show all deployments
    }),
    filteredDeployments: makeQueryFilterIn({
      items: filteredDeployments ?? [],
      filterKey: "deployment_id",
      byItemKey: "id",
    }),
  };
};

export const useCreateDeployment = spawnCreateMutationHook({
  mutationFn: createDeployment,
  notifyEntity: entity,
  analyticsEntity: apiConfigDeployments.table,
  invalidationQueryKeys: [MASTER_INVALIDATION_KEY],
});
export const useUpdateDeployment = (id?: string) =>
  spawnUpdateMutationHook({
    mutationFn: updateDeployment,
    notifyEntity: entity,
    analyticsEntity: apiConfigDeployments.table,
    entityQueryKey: deploymentsQueryKeys.byId(id),
    invalidationQueryKeys: [deploymentsQueryKeys.lists()],
  })();

export const useDeleteDeployment = spawnDeleteMutationHook({
  mutationFn: deleteDeployment,
  notifyEntity: entity,
  analyticsEntity: apiConfigDeployments.table,
  invalidationQueryKeys: [MASTER_INVALIDATION_KEY],
});

export const useCreateCustomerDeployment = spawnCreateMutationHook({
  mutationFn: createCustomerDeployment,
  analyticsEntity: apiConfigDeployments.table,
  onSuccess: () => {
    notify.success(i18n.t("notify.accept.success", { e: entity }));
  },
  onError: () => notify.error(i18n.t("notify.error-reason.unexpected"), i18n.t("notify.accept.error", { e: entity })),
  invalidationQueryKeys: [MASTER_INVALIDATION_KEY],
});
